// src/pages/admin/tabs/amenities/AmenitiesListPage.jsx

import React from 'react';
import styled from 'styled-components';
import { Link, useNavigate } from 'react-router-dom';
import { AdminAPI } from '../../../../api/admin';
import { normalizeMediaUrl } from '../../../../api/base';
import { Card, Row2, Field, Actions, Btn, Badge, TableWrap, Table, Paginator, PageBtn } from '../../ui';

const Wrap = styled.div`display:grid; gap:20px;`;

const DEFAULT_Q = { page: 1, page_size: 20, is_active: '', q: '', locale: 'ru' };

function title(a){
  return a.name ?? a.name_ru ?? a.name_en ?? a.slug ?? `#${a.id}`;
}
function iconOf(a){
  return a.icon || '';
}

export default function AmenitiesListPage(){
  const [query, setQuery] = React.useState(DEFAULT_Q);
  const [items, setItems] = React.useState([]);
  const [total, setTotal] = React.useState(0);
  const [loading, setLoading] = React.useState(false);
  const [err, setErr] = React.useState('');
  const nav = useNavigate();

  const totalPages = Math.max(1, Math.ceil((total || items.length) / (query.page_size || 20)));

  const load = React.useCallback(async ()=>{
    setLoading(true); setErr('');
    try{
      const d = await AdminAPI.listAmenities({
        page: query.page,
        page_size: query.page_size,
        is_active: query.is_active || undefined,
        q: query.q || undefined,
        locale: query.locale || 'ru',
      });
      const list = d?.items || d?.amenities || [];
      const tot  = d?.pagination?.total ?? list.length;
      setItems(list); setTotal(tot);
    }catch(e){
      console.error(e);
      setItems([]); setTotal(0);
      setErr(
        e?.payload?.details
          ? (typeof e.payload.details === 'string' ? e.payload.details : JSON.stringify(e.payload.details))
          : (e?.payload?.error?.message || e?.payload?.message || e?.message || 'Не удалось загрузить удобства')
      );
    }finally{ setLoading(false); }
  }, [query]);

  React.useEffect(()=>{ load(); }, [load]);

  const goPage = (n) => setQuery(q => ({ ...q, page: Math.max(1, Math.min(n, totalPages)) }));

  const del = async (a)=>{
    if(!window.confirm('Удалить удобство?')) return;
    try{ await AdminAPI.deleteAmenity(a.id); await load(); }
    catch(e){ alert(e?.payload?.error || e?.message || 'Не удалось удалить'); }
  };

  return (
    <Wrap>
      <Card>
        <h3>Удобства</h3>
        <Actions style={{marginTop:8, gap:10, flexWrap:'wrap', alignItems:'end'}}>
          <Field>Поиск
            <input value={query.q} onChange={e=>setQuery(q=>({...q, q:e.target.value, page:1}))} placeholder="имя / слаг / категория" />
          </Field>
          <Row2>
            <Field>Активно
              <select value={query.is_active} onChange={e=>setQuery(q=>({...q, is_active:e.target.value, page:1}))}>
                <option value="">все</option>
                <option value="true">да</option>
                <option value="false">нет</option>
              </select>
            </Field>
            <Field>На странице
              <select value={query.page_size} onChange={e=>setQuery(q=>({...q, page_size:Number(e.target.value), page:1}))}>
                <option value={10}>10</option><option value={20}>20</option><option value={50}>50</option><option value={100}>100</option>
              </select>
            </Field>
          </Row2>
          <Btn onClick={()=>setQuery(DEFAULT_Q)}>Сброс</Btn>
          <Btn onClick={load}>Обновить</Btn>
          <Badge>Найдено: <b>{total || items.length}</b></Badge>
          <div style={{flex:1}} />
          <Btn $kind="primary" onClick={()=>nav('/admin/amenities/new')}>+ Создать удобство</Btn>
        </Actions>
      </Card>

      <Card>
        <h3>Список {loading ? '(загрузка...)' : ''}</h3>
        {err && <div style={{color:'#e66', marginBottom:8}}>{err}</div>}
        <TableWrap>
          <Table>
            <thead>
              <tr>
                <th>ID</th>
                <th>Изображение</th>
                <th>Иконка</th>
                <th>Название</th>
                <th>Активно</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {items.map(a=> (
                <tr key={a.id}>
                  <td>{a.id}</td>
                  <td>
                    {a.cover_thumb ? (
                      <img src={normalizeMediaUrl(a.cover_thumb)} alt="" style={{width:40, height:40, objectFit:'cover', borderRadius:8}}/>
                    ) : (
                      <div style={{width:40, height:40, background:'#f0f0f0', borderRadius:8, display:'flex', alignItems:'center', justifyContent:'center', fontSize:12, color:'#999'}}>
                        Нет
                      </div>
                    )}
                  </td>
                  <td style={{fontSize:24}}>{iconOf(a)}</td>
                  <td style={{maxWidth:280}}>
                    <div style={{fontWeight:700}}>{title(a)}</div>
                    {a.description && <div style={{fontSize:12, color:'#666', marginTop:4}}>{a.description}</div>}
                  </td>
                  <td>{(a.is_active ?? true) ? 'Да' : 'Нет'}</td>
                  <td style={{display:'flex', gap:8}}>
                    <Link className="btn" to={`/admin/amenities/${a.id}`}>Редактировать</Link>
                    <Btn onClick={()=>del(a)}>Удалить</Btn>
                  </td>
                </tr>
              ))}
              {!items.length && !loading && (
                <tr><td colSpan={6} style={{opacity:.7}}>Нет данных</td></tr>
              )}
            </tbody>
          </Table>
        </TableWrap>

        <Paginator>
          <PageBtn onClick={()=>goPage(1)} disabled={query.page<=1}>« Первая</PageBtn>
          <PageBtn onClick={()=>goPage(query.page-1)} disabled={query.page<=1}>‹ Назад</PageBtn>
          <PageBtn data-active="true">{query.page} / {totalPages}</PageBtn>
          <PageBtn onClick={()=>goPage(query.page+1)} disabled={query.page>=totalPages}>Вперёд ›</PageBtn>
          <PageBtn onClick={()=>goPage(totalPages)} disabled={query.page>=totalPages}>Последняя »</PageBtn>
        </Paginator>
      </Card>
    </Wrap>
  );
}
